import React, { useCallback, useEffect, useState } from 'react';
import api from 'api';
import { executeMobilePurchase, notifyMobilePurchaseComplete } from './mobilePurchaseFlow';

const translateTemplate = (translate, stringNumber, fallback, values = {}) => {
  const template = typeof translate === 'function' ? translate(stringNumber, fallback, true) : fallback;
  return Object.entries(values).reduce(
    (text, [key, value]) => String(text).replace(new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'gi'), String(value)),
    String(template || fallback || '')
  );
};

const statusText = (status, translate) => {
  const type = status?.purchaseProductType;
  if (type === 'custom_content') {
    const current = Math.max(0, Number(status?.currentCustomLevelIndex || 0));
    return current
      ? translateTemplate(translate, 3857, 'Custom Level {{current}} is active. Buy Custom Level {{next}}.', { current, next: current + 1 })
      : translateTemplate(translate, 3858, 'Complete Placement Assessment before buying your next Custom Level.');
  }
  if (type === 'subscription' || type === 'org_subscription') {
    const days = Math.max(0, Number(status?.daysRemaining || 0));
    return days === 1
      ? translateTemplate(translate, 3859, '1 day remains in your subscription.')
      : translateTemplate(translate, 3860, '{{days}} days remain in your subscription.', { days });
  }
  return '';
};

const MobilePurchasePanel = ({ translate, initialStatus = null, onStatusChange }) => {
  const [status, setStatus] = useState(initialStatus);
  const [loading, setLoading] = useState(!initialStatus);
  const [buying, setBuying] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const next = await api.mobileRuntime.getPurchaseStatus();
      setStatus(next);
      onStatusChange && onStatusChange(next);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || translateTemplate(translate, 3856, 'Purchase status could not be loaded.'));
    } finally {
      setLoading(false);
    }
  }, [onStatusChange, translate]);

  useEffect(() => {
    if (!initialStatus) load();
  }, [initialStatus, load]);

  const buy = async () => {
    if (!status?.purchase || !status?.canBuy || buying) return;
    setBuying(true);
    setError('');
    try {
      const result = await executeMobilePurchase(status);
      setStatus(result);
      onStatusChange && onStatusChange(result);
      await notifyMobilePurchaseComplete(result, translate);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || translateTemplate(translate, 3861, 'Purchase could not be completed.'));
    } finally {
      setBuying(false);
    }
  };

  if (loading) return <div style={{ padding: 18 }}>{translateTemplate(translate, 3854, 'Loading purchase...')}</div>;
  if (!status?.configured) return <div style={{ padding: 18 }}>{error || translateTemplate(translate, 3855, 'No Mobile Purchase is configured.')}</div>;

  const price = Number(status?.purchase?.price || 0);
  const currency = status?.purchase?.currency || '';
  return (
    <div style={{ padding: 18, maxWidth: 620, margin: '0 auto', textAlign: 'center' }}>
      <h2 style={{ marginTop: 0 }}>{status?.purchase?.title || translate?.(3850, 'Buy', true) || 'Buy'}</h2>
      {statusText(status, translate) && <p>{statusText(status, translate)}</p>}
      {price > 0 && <p style={{ fontWeight: 700 }}>{price} {currency}</p>}
      {error && <div style={{ color: '#b00020', marginBottom: 12 }}>{error}</div>}
      {status.canBuy && (
        <button type="button" onClick={buy} disabled={buying} style={{ minWidth: 150, minHeight: 44, cursor: buying ? 'default' : 'pointer' }}>
          {buying ? (translate?.(9994, 'Processing...', true) || 'Processing...') : (translate?.(3850, 'Buy', true) || 'Buy')}
        </button>
      )}
    </div>
  );
};

export default MobilePurchasePanel;
